import { useEffect, useState } from "react";
import { Trash2, Search, X, Calendar, Users, Phone, Mail, MapPin, CreditCard, MessageSquare, FileDown, Plus, Pencil } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY_NEW = {
  room_id: "", check_in: "", check_out: "", guests: 2,
  guest_name: "", guest_email: "", guest_phone: "",
  address_line: "", city: "", postal_code: "", country: "",
  special_requests: "",
  source: "staff",         // where the booking came from
  status: "confirmed",     // pending / confirmed / cancelled / no_show
  payment_status: "unpaid",// default UNPAID — staff settle manually (was auto-paid before)
  policy_choice: "Flexible",
  total_override: "",      // optional — empty means auto-calc from per-night pricing
  // Cost breakdown (informational; does NOT change the guest total)
  commission_amount: "", commission_note: "",
  vat_amount: "", city_tax: "",
  damage_deposit: "", cleaning_fee: "",
  internal_notes: "",
};

const SOURCE_OPTIONS = [
  { value: "staff", label: "Staff (direct)" },
  { value: "walkin", label: "Walk-in" },
  { value: "phone", label: "Phone" },
  { value: "website", label: "Website" },
  { value: "bookingcom", label: "Booking.com" },
  { value: "airbnb", label: "Airbnb" },
  { value: "agoda", label: "Agoda" },
  { value: "expedia", label: "Expedia" },
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-show" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "unpaid", label: "Unpaid" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "refunded", label: "Refunded" },
];

const POLICY_OPTIONS = [
  "Flexible", "Standard", "Non-refundable", "Long-stay", "Custom",
];

export default function StaffBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);   // booking object being edited (null = closed)
  const [rooms, setRooms] = useState([]);
  const [newForm, setNewForm] = useState(EMPTY_NEW);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => { api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {}); }, []);

  function load() {
    const url = debouncedQ ? `/admin/bookings?q=${encodeURIComponent(debouncedQ)}` : "/admin/bookings";
    api.get(url).then((r) => setBookings(r.data));
  }
  useEffect(load, [debouncedQ]);

  async function cancel(id) {
    if (!confirm("Cancel this booking?")) return;
    await api.post(`/admin/bookings/${id}/cancel`);
    toast.success("Cancelled");
    load();
  }
  async function del(id) {
    if (!confirm("Permanently DELETE this booking? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  // Convert a UI form value to API payload — empty strings → null for numeric
  // / optional fields so the backend's `_opt_float` doesn't reject them.
  function _toApiPayload(form) {
    const numericKeys = [
      "total_override", "commission_amount", "vat_amount",
      "city_tax", "damage_deposit", "cleaning_fee",
    ];
    const out = { ...form, guests: Number(form.guests || 1) };
    for (const k of numericKeys) {
      out[k] = form[k] === "" || form[k] == null ? null : Number(form[k]);
    }
    return out;
  }

  async function createBooking() {
    if (!newForm.room_id || !newForm.check_in || !newForm.check_out || !newForm.guest_name || !newForm.guest_email) {
      toast.error("Room, dates, guest name and email are required"); return;
    }
    setCreating(true);
    try {
      await api.post("/admin/bookings", _toApiPayload(newForm));
      toast.success("Booking created");
      setShowNew(false); setNewForm(EMPTY_NEW); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  }

  async function saveEdit(form) {
    if (!editing) return;
    try {
      // Build a PATCH payload containing only fields that actually changed
      // — keeps the request small and the audit trail readable.
      const payload = {};
      const allKeys = Object.keys(EMPTY_NEW);
      for (const k of allKeys) {
        const before = editing[k] ?? "";
        const after = form[k] ?? "";
        if (String(before) !== String(after)) payload[k] = after;
      }
      // Coerce numerics + guests
      const numericKeys = ["total_override", "commission_amount", "vat_amount", "city_tax", "damage_deposit", "cleaning_fee"];
      for (const k of numericKeys) {
        if (k in payload) payload[k] = payload[k] === "" ? null : Number(payload[k]);
      }
      if ("guests" in payload) payload.guests = Number(payload.guests || 1);

      if (Object.keys(payload).length === 0) {
        toast("No changes to save");
        setEditing(null); return;
      }
      await api.patch(`/admin/bookings/${editing.id}`, payload);
      toast.success("Booking updated");
      setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }

  const visible = bookings.filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (sourceFilter !== "all" && (b.source || "website") !== sourceFilter) return false;
    return true;
  });

  return (
    <StaffLayout>
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <h1 className="font-display text-4xl">Bookings</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-80">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <input
              data-testid="bookings-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search ref · name · email · PIN · phone"
              className="w-full pl-9 pr-9 py-2.5 border border-[#E4E4E7] rounded-sm text-sm bg-white"
            />
            {q && (
              <button onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-[#A1A1AA] hover:text-[#52525B]">
                <X size={14} />
              </button>
            )}
          </div>
          <button
            data-testid="bookings-new"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-brand-primary text-white text-sm px-4 py-2.5 rounded-sm hover:bg-brand-primaryHover whitespace-nowrap"
          >
            <Plus size={14} /> New booking
          </button>
        </div>
      </div>

      <div className="mt-4 flex gap-2 text-xs flex-wrap">
        {["all", "pending", "confirmed", "cancelled", "no_show"].map((s) => (
          <button key={s} data-testid={`bookings-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-sm uppercase tracking-wider ${filter === s ? "bg-brand-primary text-white" : "bg-white border border-[#E4E4E7]"}`}>{s}</button>
        ))}
        <span className="ml-auto text-[#71717A] self-center">{visible.length} result{visible.length !== 1 ? "s" : ""}</span>
      </div>

      <div className="mt-2 flex gap-2 text-xs flex-wrap items-center">
        <span className="text-[10px] uppercase tracking-wider text-[#71717A] mr-1">Source</span>
        {[
          { k: "all", l: "All" },
          { k: "website", l: "Site" },
          { k: "external", l: "OTA" },
          { k: "staff", l: "Staff" },
        ].map((s) => (
          <button key={s.k} data-testid={`bookings-source-filter-${s.k}`} onClick={() => setSourceFilter(s.k)}
            className={`px-3 py-1 rounded-sm uppercase tracking-wider ${sourceFilter === s.k ? "bg-brand-primary text-white" : "bg-white border border-[#E4E4E7]"}`}>
            {s.l}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white border border-[#E4E4E7] rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-[#FAFAFA] text-[#71717A] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Ref / Guest</th>
              <th className="text-left p-4">Source</th>
              <th className="text-left p-4">PIN</th>
              <th className="text-left p-4">Room</th>
              <th className="text-left p-4">Dates</th>
              <th className="text-left p-4">Total</th>
              <th className="text-left p-4">Card</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Payment</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F4F4F5]">
            {visible.map((b) => (
              <tr key={b.id} data-testid={`staff-booking-${b.id}`} onClick={() => setSelected(b)} className="cursor-pointer hover:bg-[#FAFAFA]">
                <td className="p-4">
                  <div className="font-mono text-xs font-semibold">{b.booking_ref || `#${b.id.slice(0, 8)}`}</div>
                  <div className="font-medium mt-0.5">{b.guest_name}</div>
                  <div className="text-xs text-[#71717A]">{b.guest_email}</div>
                </td>
                <td className="p-4"><SourceChip src={b.source} /></td>
                <td className="p-4 font-mono text-xs tracking-wider">{b.pin || "—"}</td>
                <td className="p-4">{b.room_name}</td>
                <td className="p-4 text-xs">{b.check_in} → {b.check_out}<br /><span className="text-[#71717A]">{b.nights} nights · {b.guests} guests</span></td>
                <td className="p-4 font-medium">{fmtMoney(b.total)}</td>
                <td className="p-4">
                  {b.card_last4 ? (
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className="uppercase font-medium tracking-wider">{b.card_brand || "card"}</span>
                      <span className="font-mono">····{b.card_last4}</span>
                    </span>
                  ) : <span className="text-xs text-[#A1A1AA]">—</span>}
                </td>
                <td className="p-4"><Tag value={b.status} /></td>
                <td className="p-4"><Tag value={b.payment_status} /></td>
                <td className="p-4 text-right space-x-1 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    data-testid={`staff-edit-${b.id}`}
                    onClick={() => setEditing(b)}
                    title="Edit booking (status, payment, policy, breakdown…)"
                    className="p-1.5 hover:bg-zinc-100 text-zinc-700 rounded-sm"
                  >
                    <Pencil size={13} />
                  </button>
                  {b.status !== "cancelled" && (
                    <button data-testid={`staff-cancel-${b.id}`} onClick={() => cancel(b.id)} className="text-xs text-rose-600 hover:underline">Cancel</button>
                  )}
                  <button data-testid={`staff-delete-booking-${b.id}`} onClick={() => del(b.id)} title="Delete forever" className="p-1.5 hover:bg-rose-50 text-rose-700 rounded-sm"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-[#71717A]">{debouncedQ ? `No bookings match "${debouncedQ}"` : "No bookings."}</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <CustomerCartelModal booking={selected} onClose={() => setSelected(null)} onCancel={() => { cancel(selected.id); setSelected(null); }} onDelete={() => del(selected.id)} />}
      {showNew && (
        <BookingFormModal
          mode="create"
          initial={EMPTY_NEW}
          rooms={rooms}
          busy={creating}
          onClose={() => setShowNew(false)}
          onSubmit={(form) => { setNewForm(form); createBooking(); }}
          form={newForm}
          setForm={setNewForm}
        />
      )}
      {editing && (
        <BookingFormModal
          mode="edit"
          initial={{ ...EMPTY_NEW, ...editing }}
          rooms={rooms}
          busy={false}
          onClose={() => setEditing(null)}
          onSubmit={(form) => saveEdit(form)}
        />
      )}
    </StaffLayout>
  );
}

function NbInput({ label, type = "text", v, on, tid, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</span>
      <input
        data-testid={tid}
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        className="mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm"
      />
    </label>
  );
}

/* Reusable create/edit modal. `mode` toggles labels + submit copy + the
   hint banner. `initial` seeds the form on open; in `edit` mode the parent's
   `form` / `setForm` are unused (we keep local state) so each edit is isolated. */
function BookingFormModal({ mode, initial, rooms, busy, onClose, onSubmit, form: extForm, setForm: extSetForm }) {
  const isEdit = mode === "edit";
  // Create-mode uses the parent's `newForm` state; edit-mode keeps state local.
  const [localForm, setLocalForm] = useState(initial);
  const form = isEdit ? localForm : extForm;
  const setForm = isEdit ? setLocalForm : extSetForm;
  const [showBreakdown, setShowBreakdown] = useState(false);

  const upd = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <div data-testid={`booking-${mode}-modal`} className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-none sm:rounded-md max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E4E4E7] px-6 py-4 flex items-center justify-between z-10">
          <div className="font-display text-xl">
            {isEdit ? `Edit booking · ${initial.booking_ref || initial.id?.slice(0, 8) || ""}` : "New booking"}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#FAFAFA]"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 grid sm:grid-cols-2 gap-3 text-sm">
          {/* Room / stay */}
          <label className="sm:col-span-2 block">
            <span className="text-[10px] uppercase tracking-wider text-[#71717A]">Room *</span>
            <select data-testid={`${mode}-room`} value={form.room_id} disabled={isEdit}
              onChange={(e) => setForm({ ...form, room_id: e.target.value })}
              className="mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm bg-white disabled:bg-[#FAFAFA] disabled:text-[#71717A]">
              <option value="">Select a room…</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {isEdit && <span className="text-[10px] text-[#A1A1AA] mt-1 inline-block">Room cannot be changed after creation.</span>}
          </label>
          <NbInput label="Check-in *" type="date" v={form.check_in || ""} on={upd("check_in")} tid={`${mode}-ci`} />
          <NbInput label="Check-out *" type="date" v={form.check_out || ""} on={upd("check_out")} tid={`${mode}-co`} />
          <NbInput label="Guests *" type="number" v={form.guests} on={upd("guests")} tid={`${mode}-guests`} />
          <NbInput label="Guest name *" v={form.guest_name || ""} on={upd("guest_name")} tid={`${mode}-name`} />
          <NbInput label="Email *" type="email" v={form.guest_email || ""} on={upd("guest_email")} tid={`${mode}-email`} />
          <NbInput label="Phone" v={form.guest_phone || ""} on={upd("guest_phone")} tid={`${mode}-phone`} />
          <NbInput label="Address" v={form.address_line || ""} on={upd("address_line")} tid={`${mode}-address`} full />
          <NbInput label="City" v={form.city || ""} on={upd("city")} tid={`${mode}-city`} />
          <NbInput label="Postal code" v={form.postal_code || ""} on={upd("postal_code")} tid={`${mode}-postal`} />
          <NbInput label="Country" v={form.country || ""} on={upd("country")} tid={`${mode}-country`} full />

          {/* Status & payment */}
          <div className="sm:col-span-2 mt-2 pt-3 border-t border-[#F4F4F5] text-[11px] uppercase tracking-wider text-[#A1A1AA]">
            Status & payment
          </div>
          <SelectField label="Status" tid={`${mode}-status`} value={form.status || "confirmed"} onChange={upd("status")} options={STATUS_OPTIONS} />
          <SelectField label="Payment status" tid={`${mode}-payment-status`} value={form.payment_status || "unpaid"} onChange={upd("payment_status")} options={PAYMENT_STATUS_OPTIONS} />
          <SelectField label="Source / channel" tid={`${mode}-source`} value={form.source || "staff"} onChange={upd("source")} options={SOURCE_OPTIONS} />
          <SelectField label="Policy / rate plan" tid={`${mode}-policy`} value={form.policy_choice || "Flexible"} onChange={upd("policy_choice")} options={POLICY_OPTIONS.map((p) => ({ value: p, label: p }))} />

          {/* Pricing */}
          <label className="sm:col-span-2 block mt-2 pt-3 border-t border-[#F4F4F5]">
            <span className="text-[10px] uppercase tracking-wider text-[#71717A]">
              Total price (€) <span className="normal-case text-[#A1A1AA]">— leave empty to auto-calc from per-night pricing</span>
            </span>
            <input
              data-testid={`${mode}-total-override`}
              type="number" inputMode="decimal" min="0" step="0.01"
              value={form.total_override ?? ""}
              onChange={(e) => setForm({ ...form, total_override: e.target.value })}
              placeholder={isEdit ? `current total: €${(initial.total ?? 0).toFixed(2)}` : "e.g. 320.00"}
              className="mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm"
            />
          </label>

          {/* Cost breakdown (collapsible) */}
          <div className="sm:col-span-2">
            <button type="button" onClick={() => setShowBreakdown((v) => !v)}
              data-testid={`${mode}-toggle-breakdown`}
              className="text-xs text-brand-primary hover:underline">
              {showBreakdown ? "− Hide cost breakdown" : "+ Add cost breakdown (commission, VAT, city tax, deposit, cleaning fee)"}
            </button>
          </div>
          {showBreakdown && (
            <>
              <NbInput label="Commission you pay (€)" type="number" v={form.commission_amount ?? ""} on={upd("commission_amount")} tid={`${mode}-commission`} />
              <NbInput label="Commission note" v={form.commission_note || ""} on={upd("commission_note")} tid={`${mode}-commission-note`} />
              <NbInput label="VAT amount (€)" type="number" v={form.vat_amount ?? ""} on={upd("vat_amount")} tid={`${mode}-vat`} />
              <NbInput label="City / tourism tax (€)" type="number" v={form.city_tax ?? ""} on={upd("city_tax")} tid={`${mode}-city-tax`} />
              <NbInput label="Damage deposit (€)" type="number" v={form.damage_deposit ?? ""} on={upd("damage_deposit")} tid={`${mode}-damage`} />
              <NbInput label="Cleaning fee (€)" type="number" v={form.cleaning_fee ?? ""} on={upd("cleaning_fee")} tid={`${mode}-cleaning`} />
            </>
          )}

          <NbInput label="Notes / special requests (guest-visible)" v={form.special_requests || ""} on={upd("special_requests")} tid={`${mode}-notes`} full />
          <NbInput label="Internal notes (staff-only)" v={form.internal_notes || ""} on={upd("internal_notes")} tid={`${mode}-internal`} full />
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E4E4E7] px-6 py-4 flex items-center justify-between gap-2">
          <div className="text-xs text-[#71717A]">
            {isEdit
              ? <>Changes apply immediately. Marking <strong>paid</strong> stamps a timestamp.</>
              : <>Defaults to <strong>confirmed · unpaid</strong>. Pick anything you like above.</>}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm border border-[#E4E4E7] px-4 py-2 rounded-md hover:bg-[#FAFAFA]">Cancel</button>
            <button
              data-testid={`${mode}-save`}
              onClick={() => onSubmit(form)}
              disabled={busy}
              className="text-sm bg-brand-primary text-white px-4 py-2 rounded-md hover:bg-brand-primaryHover disabled:opacity-50"
            >
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Create booking")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectField({ label, tid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</span>
      <select
        data-testid={tid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm bg-white"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function CustomerCartelModal({ booking, onClose, onCancel, onDelete }) {
  const b = booking;
  const [showRefund, setShowRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  async function doRefund(full) {
    setRefunding(true);
    try {
      const payload = full ? { reason: refundReason } : { amount: Number(refundAmount), reason: refundReason };
      const r = await api.post(`/admin/bookings/${b.id}/refund`, payload);
      toast.success(`Refunded €${r.data.amount.toFixed(2)} ${r.data.full ? "(full)" : "(partial)"}`);
      setShowRefund(false);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally { setRefunding(false); }
  }

  const [resending, setResending] = useState(false);
  async function doResend() {
    setResending(true);
    try {
      const r = await api.post(`/admin/bookings/${b.id}/resend-confirmation`);
      toast.success(r.data.email_sent ? "Confirmation email sent" : "Email skipped (Resend domain not verified)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally { setResending(false); }
  }

  const [markingPaid, setMarkingPaid] = useState(false);
  async function markPaid() {
    const method = window.prompt("Payment method? (cash / bank_transfer / pos / other)", "cash");
    if (!method) return;
    if (!window.confirm(`Mark booking ${b.booking_ref} as PAID by ${method}? This will send the guest a confirmation.`)) return;
    setMarkingPaid(true);
    try {
      await api.post(`/admin/bookings/${b.id}/mark-paid`, { method });
      toast.success("Marked as paid · confirmation sent");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setMarkingPaid(false); }
  }

  const [markingNoShow, setMarkingNoShow] = useState(false);
  async function markNoShow() {
    const note = window.prompt("Optional note (internal):", "");
    if (note === null) return;
    if (!window.confirm(`Mark booking ${b.booking_ref} as NO-SHOW?`)) return;
    setMarkingNoShow(true);
    try {
      await api.post(`/admin/bookings/${b.id}/mark-no-show`, { note });
      toast.success("Marked as no-show");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setMarkingNoShow(false); }
  }
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-none sm:rounded-md max-h-screen overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-[#E4E4E7] px-6 py-4 flex justify-between items-center z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Guest cartel</div>
            <h2 className="font-display text-2xl mt-0.5">{b.guest_name}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#FAFAFA] rounded-sm" data-testid="cartel-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6 text-sm">
          {/* Reference / PIN block */}
          <div className="bg-brand-secondary/40 border border-brand-primary/20 rounded-md p-4 flex flex-wrap gap-6 justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#71717A]">Booking reference</div>
              <div className="font-mono text-xl font-semibold tracking-wider mt-1">{b.booking_ref || `#${b.id.slice(0, 8)}`}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#71717A]">Access PIN</div>
              <div className="font-mono text-xl font-semibold text-brand-primary tracking-[0.2em] mt-1">{b.pin || "—"}</div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Tag value={b.status} />
              <Tag value={b.payment_status} />
            </div>
          </div>

          <Section title="Contact">
            <Field icon={<Mail size={13} />} value={b.guest_email} copyable />
            <Field icon={<Phone size={13} />} value={b.guest_phone || "—"} copyable={!!b.guest_phone} />
          </Section>

          {(b.address_line || b.city || b.country) && (
            <Section title="Billing address">
              <div className="text-brand-text">
                {b.address_line || <span className="text-[#A1A1AA]">No street</span>}
                {(b.address_line) && <br />}
                {[b.city, b.postal_code].filter(Boolean).join(" ")}
                {b.country ? `, ${b.country}` : ""}
              </div>
            </Section>
          )}

          <Section title="Stay">
            <Field icon={<Calendar size={13} />} label="Check-in" value={b.check_in} />
            <Field icon={<Calendar size={13} />} label="Check-out" value={b.check_out} />
            <Field icon={<MapPin size={13} />} label="Room" value={b.room_name} />
            <Field icon={<Users size={13} />} label="Guests" value={`${b.guests} guest${b.guests !== 1 ? "s" : ""} · ${b.nights} night${b.nights !== 1 ? "s" : ""}`} />
          </Section>

          <Section title="Payment">
            <Field label="Subtotal" value={fmtMoney(b.subtotal)} />
            <Field label="Taxes" value={fmtMoney(b.taxes)} />
            <Field label="Total" value={<strong className="text-brand-primary">{fmtMoney(b.total)}</strong>} />
            {b.card_last4 && (
              <Field icon={<CreditCard size={13} />} label="Card" value={`${b.card_brand || "card"} ····${b.card_last4}`} />
            )}
          </Section>

          {b.special_requests && (
            <Section title="Special requests">
              <div className="italic text-brand-muted flex items-start gap-2"><MessageSquare size={14} className="mt-0.5 shrink-0" /> &ldquo;{b.special_requests}&rdquo;</div>
            </Section>
          )}

          <div className="text-[10px] text-[#A1A1AA] pt-2 border-t border-[#E4E4E7]">
            Booked {new Date(b.created_at).toLocaleString()} · ID {b.id}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[#E4E4E7] px-6 py-4 flex flex-wrap gap-2 justify-end">
          <a
            href={`${BACKEND}/api/admin/bookings/${b.id}/invoice`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cartel-invoice"
            className="text-sm inline-flex items-center gap-1.5 border border-[#E4E4E7] text-brand-text px-4 py-2 rounded-sm hover:bg-[#FAFAFA]"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const token = localStorage.getItem("berdhill_token");
                const resp = await fetch(`${BACKEND}/api/admin/bookings/${b.id}/invoice`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!resp.ok) throw new Error("Failed");
                const blob = await resp.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
              } catch {
                toast.error("Unable to download invoice");
              }
            }}
          >
            <FileDown size={13} /> Invoice PDF
          </a>
          <button
            onClick={doResend}
            data-testid="cartel-resend"
            disabled={resending}
            className="text-sm inline-flex items-center gap-1.5 border border-[#E4E4E7] text-brand-text px-4 py-2 rounded-sm hover:bg-[#FAFAFA] disabled:opacity-50"
          >
            <Mail size={13} /> {resending ? "Resending…" : "Resend confirmation"}
          </button>
          {b.payment_status === "paid" && (
            <button
              onClick={() => setShowRefund(true)}
              data-testid="cartel-refund-open"
              className="text-sm inline-flex items-center gap-1.5 border border-amber-300 text-amber-800 px-4 py-2 rounded-sm hover:bg-amber-50"
            >
              ↩ Refund
            </button>
          )}
          {b.status !== "cancelled" && (
            <button onClick={onCancel} data-testid="cartel-cancel" className="text-sm border border-rose-300 text-rose-700 px-4 py-2 rounded-sm hover:bg-rose-50">Cancel booking</button>
          )}
          <button onClick={onDelete} data-testid="cartel-delete" className="text-sm bg-rose-700 hover:bg-rose-800 text-white px-4 py-2 rounded-sm">Delete forever</button>
        </div>
      </div>

      {showRefund && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => !refunding && setShowRefund(false)}>
          <div className="bg-white rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">Refund booking {b.booking_ref}</h3>
            <p className="text-sm text-[#71717A] mb-4">Original total: <strong>€{(b.total || 0).toFixed(2)}</strong>. Refunds are processed via SumUp and typically settle to the card within 5–10 business days.</p>
            <label className="block mb-3">
              <span className="text-[10px] uppercase tracking-wider text-[#71717A]">Partial amount (EUR) — leave empty for full</span>
              <input
                data-testid="refund-amount"
                type="number"
                step="0.01"
                min="0"
                max={b.total || undefined}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder="Empty = full refund"
                className="mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm"
              />
            </label>
            <label className="block mb-4">
              <span className="text-[10px] uppercase tracking-wider text-[#71717A]">Reason (internal note)</span>
              <input
                data-testid="refund-reason"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Cancellation, overbooking, damage waived…"
                className="mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowRefund(false)} disabled={refunding} className="text-sm border border-[#E4E4E7] px-4 py-2 rounded-md hover:bg-[#FAFAFA]">Cancel</button>
              {refundAmount ? (
                <button data-testid="refund-do-partial" onClick={() => doRefund(false)} disabled={refunding} className="text-sm bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md disabled:opacity-50">{refunding ? "Refunding…" : `Refund €${Number(refundAmount).toFixed(2)}`}</button>
              ) : (
                <button data-testid="refund-do-full" onClick={() => doRefund(true)} disabled={refunding} className="text-sm bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 rounded-md disabled:opacity-50">{refunding ? "Refunding…" : "Full refund"}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Field({ icon, label, value, copyable }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-[#71717A] flex items-center gap-1.5 shrink-0">{icon}{label}</span>
      <span className="text-right text-brand-text flex items-center gap-2">
        {value}
        {copyable && typeof value === "string" && (
          <button onClick={() => navigator.clipboard.writeText(value)} className="text-[10px] text-brand-primary hover:underline">copy</button>
        )}
      </span>
    </div>
  );
}

function Tag({ value }) {
  const map = {
    confirmed: "bg-emerald-50 text-emerald-700",
    paid: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    unpaid: "bg-amber-50 text-amber-700",
    cancelled: "bg-rose-50 text-rose-700",
    failed: "bg-rose-50 text-rose-700",
    no_show: "bg-zinc-200 text-zinc-700",
    refunded: "bg-violet-50 text-violet-700",
    partial_refund: "bg-violet-50 text-violet-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-medium ${map[value] || "bg-zinc-100 text-zinc-700"}`}>{value}</span>;
}

const SOURCE_META = {
  website: { label: "Site", cls: "bg-amber-100 text-amber-800" },
  external: { label: "OTA", cls: "bg-violet-100 text-violet-700" },
  staff: { label: "Staff", cls: "bg-emerald-100 text-emerald-700" },
  walkin: { label: "Walk-in", cls: "bg-sky-100 text-sky-700" },
  phone: { label: "Phone", cls: "bg-teal-100 text-teal-800" },
  bookingcom: { label: "Booking.com", cls: "bg-blue-100 text-blue-800" },
  airbnb: { label: "Airbnb", cls: "bg-rose-100 text-rose-700" },
  agoda: { label: "Agoda", cls: "bg-indigo-100 text-indigo-700" },
  expedia: { label: "Expedia", cls: "bg-yellow-100 text-yellow-800" },
};

function SourceChip({ src }) {
  const key = (src || "website").toLowerCase();
  const meta = SOURCE_META[key] || { label: key.toUpperCase(), cls: "bg-zinc-100 text-zinc-700" };
  return (
    <span
      data-testid={`source-chip-${key}`}
      className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-medium ${meta.cls}`}
    >
      {meta.label}
    </span>
  );
}
